import useIsMobile from 'Common/useIsMobile';
import React, { lazy } from 'react';
import { useNavigate } from 'react-router-dom';

const Login = lazy(() => import('pages/Authentication/Login'));
const Logout = lazy(() => import('pages/Authentication/LogOut'));
const UserProfile = lazy(() => import('pages/Authentication/UserProfile'));

const QRCode = lazy(() => import('pages/QRCode'));
const Pages404 = lazy(() => import('pages/Components/Pages404'));
const SystemHistory = lazy(() => import('pages/SystemHistory'));

const AccountManagement = lazy(() => import('../pages/AccountManagement/index'));
const Customer = lazy(() => import('pages/Dept/Customer'));
const CustomerDetail = lazy(() => import('pages/Dept/Customer/CustomerDetail'));
const AddCustomer = lazy(() => import('pages/Dept/Customer/Desktop/AddCustomer'));
const Dept = lazy(() => import('pages/Dept/Dept'));
const AddDept = lazy(() => import('pages/Dept/Dept/AddDept'));
const AddDebtPayment = lazy(() => import('pages/Dept/Dept/AddDebtPayment'));
const UseServiceVoucher = lazy(() => import('pages/Dept/FuelSlipHistory/UseServiceVoucher'));

const InventoryManagement = lazy(() => import('pages/Dept/InventoryManagement'));
const InventorySetup = lazy(() => import('pages/Dept/InventoryManagement/Setup'));
const ServiceSetup = lazy(() => import('pages/Dept/ServiceManagement/Setup'));
const BatchPrice = lazy(() => import('pages/Dept/ServiceManagement/BatchPrice'));
const InventoryReport = lazy(() => import('../pages/Dept/InventoryReport'));
const PumpManagement = lazy(() => import('pages/Dept/PumpManagement'));
const StoreExpenseManagement = lazy(() => import('pages/Dept/StoreExpenseManagement'));
const AddExpense = lazy(() => import('pages/Dept/StoreExpenseManagement/AddExpense'));

const ReportOverview = lazy(() => import('pages/ReportOverview'));
const ShiftHandover = lazy(() => import('pages/ShiftHandover'));
// const ShiftStopReport = lazy(() => import('pages/ShiftHandover/Mobile/ShiftStopReport'));
const ShiftHandoverDetail = lazy(() => import('pages/ShiftHandoverDetail'));
const ShiftHandoverEdit = lazy(() => import('pages/ShiftHandoverEdit'));

const FastAccess = lazy(() => import('pages/FastAccess'));

const DefaultLanding = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  if (isMobile) {
    navigate('/fast-access', { replace: true });
    return null;
  }

  navigate('/customer', { replace: true });
  return null;
};

interface RouteObject {
  path: string;
  component: React.ComponentType<any>; // Use React.ComponentType to specify the type of the component
  exact?: boolean;
  permissionFeature?: string;
  permissionFeatures?: string[];
  allowWithoutMenu?: boolean;
}

export const routes: Array<RouteObject> = [
  { path: '/', component: DefaultLanding },

  { path: '/user-profile', component: UserProfile, allowWithoutMenu: true },

  // { path: '/report/debt', component: Report },
  { path: '/customer', component: Customer, permissionFeature: 'CUSTOMER' },
  { path: '/customer/:id', component: CustomerDetail, permissionFeature: 'CUSTOMER' },
  { path: '/add-customer', component: AddCustomer, permissionFeature: 'CUSTOMER' },
  { path: '/add-fuel-customer', component: UseServiceVoucher, permissionFeature: 'CUSTOMER' },
  { path: '/debt-data', component: Dept, permissionFeature: 'DASHBOARD_DEBT' },
  { path: '/add-debt', component: AddDept, permissionFeature: 'DASHBOARD_DEBT' },
  {
    path: '/add-debt-payment',
    component: AddDebtPayment,
    permissionFeature: 'DASHBOARD_DEBT',
  },
  {
    path: '/inventory-management',
    component: InventoryManagement,
    permissionFeature: 'INVENTORY_DOC',
  },
  { path: '/inventory/setup', component: InventorySetup, permissionFeature: 'PRODUCT' },
  { path: '/service/setup', component: ServiceSetup, permissionFeature: 'SERVICE' },
  { path: '/service/batch-price', component: BatchPrice, permissionFeature: 'SERVICE' },
  { path: '/pump-management', component: PumpManagement, permissionFeature: 'PUMP' },
  {
    path: '/inventory/report',
    component: InventoryReport,
    permissionFeature: 'DASHBOARD_INVENTORY',
  },
  { path: '/inventory/products', component: InventorySetup, permissionFeature: 'PRODUCT' },
  {
    path: '/inventory/product-categories',
    component: InventorySetup,
    permissionFeature: 'PRODUCT',
  },
  { path: '/expense-management', component: StoreExpenseManagement, allowWithoutMenu: true },
  { path: '/add-expense', component: AddExpense, allowWithoutMenu: true },
  {
    path: '/account-management',
    component: AccountManagement,
    permissionFeature: 'ACCOUNT_MANAGEMENT',
  },
  { path: '/system-history', component: SystemHistory, permissionFeature: 'HISTORY' },

  {
    path: '/shift-handovers',
    component: ShiftHandover,
    permissionFeatures: ['SHIFT_MANAGEMENT', 'SHIFT_MEMBERSHIP'],
  },
  // {
  //   path: '/shift-handovers/:id/stop',
  //   component: ShiftStopReport,
  //   permissionFeatures: ['SHIFT_MANAGEMENT', 'SHIFT_MEMBERSHIP'],
  // },
  {
    path: '/shift-handovers/:id',
    component: ShiftHandoverDetail,
    permissionFeatures: ['SHIFT_MANAGEMENT', 'SHIFT_MEMBERSHIP'],
  },
  {
    path: '/shift-handovers/:id/edit',
    component: ShiftHandoverEdit,
    permissionFeatures: ['SHIFT_MANAGEMENT', 'SHIFT_MEMBERSHIP'],
  },
  {
    path: '/statistical-report',
    component: ReportOverview,
    permissionFeature: 'DASHBOARD_INVENTORY',
  },

  { path: '/fast-access', component: FastAccess, allowWithoutMenu: true },
  { path: '/pages-404', component: Pages404, allowWithoutMenu: true },
];

export const publicRoutes = [
  { path: '/login', component: Login },
  { path: '/logout', component: Logout },
  { path: '/public/qrcode/:code', component: QRCode },
];
